import { useAuth } from '@schooly/components/authentication';
import { SchoolUserRole } from '@schooly/constants';
import React, { PropsWithChildren, useMemo } from 'react';

import { useSchool } from '../../../hooks/useSchool';
import UserMenu from '../../ui/UserMenu';
import { SchoolSelect } from './SchoolSelect/SchoolSelect';
import { SidebarStyled } from './Sidebar.styled';
import { FormattedMessage } from 'react-intl';
import { ArrowLeftIcon, SimpleButton } from '@schooly/style';
import { Box, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { ScrollableContainer } from '../ScrollableContainer/ScrollableContainer';
import { SchoolMenu } from './SchoolMenu';
import NavMenu from '../../ui/NavMenu';

type SidebarProps = PropsWithChildren<{
  className?: string;
  isCollapsed?: boolean;
  showSchoolMenu?: boolean;
}>;

const Sidebar: React.FC<SidebarProps> = ({ className, isCollapsed, children, showSchoolMenu }) => {
  const { currentSchool, isSchoolChanging, onSelectSchool, isCompleted } = useSchool();
  const { currentUser } = useAuth();

  const schools = useMemo(
    () => currentUser?.school_relations.filter((s) => s.role === SchoolUserRole.Staff) || [],
    [currentUser],
  );

  const renderSidebarContent = () => {
    if (isSchoolChanging || !currentSchool || !isCompleted) {
      return <Stack flex={1} />;
    }

    return (
      <>
        {showSchoolMenu && (
          <Link to="/">
            <Box sx={{ pl: 2.25, py: 1, my: 1, width: '100%' }}>
              <SimpleButton
                startIcon={<ArrowLeftIcon />}
                sx={{ color: 'text.primary', '&:hover': { color: 'primary.main' } }}
              >
                <FormattedMessage id="action-Back" />
              </SimpleButton>
            </Box>
          </Link>
        )}
        <ScrollableContainer>
          <Stack px={1} mt={2}>
            {showSchoolMenu ? <SchoolMenu /> : <NavMenu isCollapsed={isCollapsed ?? false} />}
          </Stack>
        </ScrollableContainer>
      </>
    );
  };

  return (
    <SidebarStyled isCollapsed={isCollapsed} className={className}>
      <SchoolSelect
        isCollapsed={isCollapsed}
        currentSchool={currentSchool}
        schools={schools}
        onChangeSchool={onSelectSchool}
        isLoading={isSchoolChanging}
      />
      {renderSidebarContent()}
      {children}
      <UserMenu isCollapsed={isCollapsed} />
    </SidebarStyled>
  );
};

export default Sidebar;
